import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Space, Tag, Typography, Spin } from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import FetchAllEmployeesByCompany from '../../services/FetchAllEmployeesByCompany';
import FetchUnavailabilities from '../../roster/FetchUnavailabilities';
import './MyProfileCalendarPage.scss';

const { Title, Text } = Typography;

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EVENT_COLORS = {
  my_leave: '#0f7f73',
  my_unavailability: '#f47f3d',
  staff_leave: '#8e5df0',
  staff_unavailability: '#5865f2',
};

function getCurrentEmployeeId(authContext) {
  const info = authContext.additionalUserInfo || {};
  return info.id || info.userId || info.employeeId || info.staffId || authContext.user?.userId || authContext.user?.username || null;
}

function getCompanyId(info) {
  return info?.companyId || info?.company?.companyId || info?.company?.id || null;
}

function parseDate(raw) {
  if (!raw) return null;
  if (dayjs.isDayjs(raw)) return raw;
  if (typeof raw === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(raw)) return dayjs(raw, 'DD-MM-YYYY');
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
}

function getRecordLabel(record) {
  if (record?.type === 'L') {
    const leaveType = record.leaveType;
    if (leaveType === 'AL') return 'Annual leave';
    if (leaveType === 'PL') return 'Personal leave';
    if (leaveType === 'LSL') return 'Long service leave';
    return 'Leave';
  }
  return record?.reason || 'Unavailability';
}

function getRecordKind(record, isCurrentEmployee) {
  if (record?.type === 'L') return isCurrentEmployee ? 'my_leave' : 'staff_leave';
  return isCurrentEmployee ? 'my_unavailability' : 'staff_unavailability';
}

export default function MyProfileCalendarPage() {
  const authContext = useAuth();
  const token = authContext.token;
  const currentEmployeeId = useMemo(() => getCurrentEmployeeId(authContext), [authContext]);
  const companyId = authContext.additionalUserInfo?.companyId || getCompanyId(authContext.additionalUserInfo);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [month, setMonth] = useState(() => dayjs().startOf('month'));

  useEffect(() => {
    const loadCalendar = async () => {
      if (!token || !companyId || !currentEmployeeId) return;
      setLoading(true);
      setError('');

      try {
        const employees = await FetchAllEmployeesByCompany(token);
        const employeeIds = new Set();
        if (Array.isArray(employees)) {
          employees.forEach((employee) => {
            const id = employee?.id || employee?.userId || employee?.employeeId || employee?.staffId;
            if (id) employeeIds.add(String(id));
          });
        }
        employeeIds.add(String(currentEmployeeId));

        const batches = await Promise.all(
          Array.from(employeeIds).map(async (employeeId) => {
            const data = await FetchUnavailabilities(token, employeeId);
            return Array.isArray(data)
              ? data.map((item) => ({
                  ...item,
                  staffId: item.staffId || employeeId,
                }))
              : [];
          })
        );

        setRecords(batches.flat());
      } catch (err) {
        console.error('Failed to load my profile calendar:', err);
        setError('Unable to load leave and unavailability records right now.');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    loadCalendar();
  }, [token, companyId, currentEmployeeId]);

  const monthDays = useMemo(() => {
    const start = month.startOf('month').startOf('week');
    const end = month.endOf('month').endOf('week');
    const days = [];
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    return days;
  }, [month]);

  const eventsByDay = useMemo(() => {
    const map = new Map();

    records.forEach((record) => {
      const start = parseDate(record.startDate);
      const end = parseDate(record.endDate || record.startDate);
      if (!start || !end) return;

      const isCurrentEmployee = String(record.staffId || '') === String(currentEmployeeId);
      const kind = getRecordKind(record, isCurrentEmployee);
      let cursor = start.startOf('day');
      const last = end.startOf('day');

      while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({
          id: `${record.id || key}-${kind}`,
          kind,
          title: getRecordLabel(record),
          staffId: record.staffId,
          starttime: record.starttime,
          endtime: record.endtime,
          leaveType: record.leaveType,
          reason: record.reason,
        });
        cursor = cursor.add(1, 'day');
      }
    });

    return map;
  }, [records, currentEmployeeId]);

  const headerTitle = month.format('MMMM YYYY');

  return (
    <div className="my-profile-calendar-page">
      <Card className="my-profile-calendar-hero" bordered={false}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text className="my-profile-calendar-overline">My Profile</Text>
          <Title level={2} className="my-profile-calendar-title">Calendar</Title>
          <Text className="my-profile-calendar-subtitle">
            Month view of your leave, other staff leave, and your unavailability.
          </Text>
          <Space wrap>
            <Button icon={<LeftOutlined />} onClick={() => setMonth((prev) => prev.subtract(1, 'month'))} />
            <Button onClick={() => setMonth(dayjs().startOf('month'))}>Today</Button>
            <Button icon={<RightOutlined />} onClick={() => setMonth((prev) => prev.add(1, 'month'))} />
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </Space>
        </Space>
      </Card>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={18}>
          <Card className="my-profile-calendar-card" title={headerTitle}>
            <Spin spinning={loading}>
              <div className="my-profile-calendar-grid my-profile-calendar-grid--header">
                {DAY_LABELS.map((label) => (
                  <div key={label} className="my-profile-calendar-dow">{label}</div>
                ))}
              </div>
              <div className="my-profile-calendar-grid">
                {monthDays.map((day) => {
                  const dayKey = day.format('YYYY-MM-DD');
                  const dayEvents = eventsByDay.get(dayKey) || [];
                  const inMonth = day.month() === month.month();
                  const isToday = day.isSame(dayjs(), 'day');

                  return (
                    <div
                      key={dayKey}
                      className={`my-profile-calendar-cell ${inMonth ? '' : 'my-profile-calendar-cell--muted'} ${isToday ? 'my-profile-calendar-cell--today' : ''}`}
                    >
                      <div className="my-profile-calendar-cell__top">
                        <span className="my-profile-calendar-cell__date">{day.date()}</span>
                        {dayEvents.length > 0 && (
                          <Tag className="my-profile-calendar-cell__count" color="blue">
                            {dayEvents.length}
                          </Tag>
                        )}
                      </div>
                      <div className="my-profile-calendar-events">
                        {dayEvents.slice(0, 4).map((event) => (
                          <div
                            key={event.id}
                            className="my-profile-calendar-event"
                            style={{ borderLeftColor: EVENT_COLORS[event.kind] || '#d9d9d9' }}
                          >
                            <Text className="my-profile-calendar-event__label">{event.title}</Text>
                            <Text className="my-profile-calendar-event__meta">
                              {event.kind === 'my_leave' ? 'My leave' : event.kind === 'my_unavailability' ? 'My unavailability' : event.kind === 'staff_leave' ? 'Staff leave' : 'Staff unavailability'}
                            </Text>
                          </div>
                        ))}
                        {dayEvents.length > 4 && (
                          <Text className="my-profile-calendar-more">+{dayEvents.length - 4} more</Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Spin>
          </Card>
        </Col>

        <Col xs={24} xl={6}>
          <Card className="my-profile-calendar-card" title="Legend">
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <LegendItem color={EVENT_COLORS.my_leave} label="My leave" />
              <LegendItem color={EVENT_COLORS.my_unavailability} label="My unavailability" />
              <LegendItem color={EVENT_COLORS.staff_leave} label="Other staff leave" />
              <LegendItem color={EVENT_COLORS.staff_unavailability} label="Other staff unavailability" />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <Space align="center">
      <span className="my-profile-calendar-legend-dot" style={{ backgroundColor: color }} />
      <Text>{label}</Text>
    </Space>
  );
}
